import { useState } from 'react';
import { Stack } from '@fluentui/react';
import Loader from '../../components/Loader/Loader';
import { clientproposalsApi } from '../../api/api';
import Main from './components/Main';
import ClientProposalModal from '../../components/Modals/ClientProposalModal';
import styles from './ClientProposal.module.css';
import { useWorkflow, WorkflowStep } from './components/useWorkflow';
import Step1 from './components/Step1';
import Step2 from './components/Step2';
import Step3 from './components/Step3';
import { toast } from 'react-toastify';

const ClientProposal = () => {
  const [loadingAIDocuments, setLoadingAIDocuments] = useState(false); // State to handle AI document loading
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]); // State to store selected files
  const [isJobDescriptionModalOpen, setIsJobDescriptionModalOpen] = useState(false); // State to handle Job Description modal
  const [selectedProposal, setSelectedProposal] = useState<any>(null);
  const [orderBy, setOrderBy] = useState({ property: 'createddate', desc: true });

  const orderByOptions = [
    { key: 'createddate', text: 'Created Date', order: 'desc' },
    { key: 'filename', text: 'Name', order: 'asc' },
    { key: 'status', text: 'Status', order: 'asc' },
  ];

  const handleUpload = async (
    filesWithJobDescriptions: {
      file: File;
      organisation: string;
      roleTitle: string;
      clientContact: string;
      contactTitle: string;
      address: string;
      suburb: string;
      state: string;
      postCode: string;
      totalRemuneration: string;
      roleType: string;
      sector: string;
      assignmentDate: string;
      candidateSearch: string;
      interviews: string;
      referencing: string;
      noticePeriod: string;
      startDate: string;
      replacementGuarantee: string;
    }[],
  ) => {
    if (filesWithJobDescriptions.length < 2) {
      alert('Please upload at least 2 documents.');
      return;
    }

    setLoadingAIDocuments(true);
    await Promise.all(
      filesWithJobDescriptions.map(
        async ({
          file,
          organisation,
          roleTitle,
          clientContact,
          contactTitle,
          address,
          suburb,
          state,
          postCode,
          totalRemuneration,
          roleType,
          sector,
          assignmentDate,
          candidateSearch,
          interviews,
          referencing,
          noticePeriod,
          startDate,
          replacementGuarantee,
        }) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('organisation', organisation);
          formData.append('roleTitle', roleTitle);
          formData.append('clientContact', clientContact);
          formData.append('contactTitle', contactTitle);
          formData.append('address', address);
          formData.append('suburb', suburb);
          formData.append('state', state);
          formData.append('postCode', postCode);
          formData.append('totalRemuneration', totalRemuneration);
          formData.append('roleType', roleType);
          formData.append('sector', sector);
          formData.append('assignmentDate', assignmentDate);
          formData.append('candidateSearch', candidateSearch);
          formData.append('interviews', interviews);
          formData.append('referencing', referencing);
          formData.append('noticePeriod', noticePeriod);
          formData.append('startDate', startDate);
          formData.append('replacementGuarantee', replacementGuarantee);

          try {
            const response = await clientproposalsApi({ formdata: formData });
            if (!response.ok) {
              toast.error('Failed to upload file:' + response.status + response.statusText);
            }
          } catch (error) {
            toast.error('Error uploading file:' + error);
          }
        },
      ),
    );

    setLoadingAIDocuments(false);
  };

  // const { currentStep, state, dispatch, goToNextStep, goToPreviousStep, resetWorkflow } = useWorkflow();
  const {
    currentStep,
    state,
    dispatch,
    goToNextStep,
    goToPreviousStep,
    resetWorkflow,
    onStepClick,
    handleStep3Submit,
    goToStep,
  } = useWorkflow();

  const renderWorkflowStep = () => {
    switch (currentStep) {
      case WorkflowStep.Step1:
        return (
          <Step1
            data={
              {
                ...selectedProposal?.details,
                filename: selectedProposal?.filename,
                type: 'file',
              } || state.step1Data
            }
            onNext={data => {
              dispatch({ type: 'SET_STEP1_DATA', payload: data });
              goToNextStep();
            }}
            onBack={goToPreviousStep}
            onStepClick={onStepClick}
          />
        );
      case WorkflowStep.Step2:
        return (
          <Step2
            data={{
              data: selectedProposal?.details || state.step2Data,
              consultants: selectedProposal?.details?.consultingTeam || state.step1Data?.consultingTeam || [],
            }}
            onNext={(data: any, roles: any) => {
              dispatch({ type: 'SET_STEP2_DATA', payload: data, roles });
              goToNextStep();
            }}
            onBack={goToPreviousStep}
            onStepClick={onStepClick}
          />
        );
      case WorkflowStep.Step3:
        return (
          <Step3
            data={selectedProposal?.details || state.step3Data}
            step2Data={selectedProposal?.details?.step2Data || state.step2Data}
            onComplete={data => {
              dispatch({ type: 'SET_STEP3_DATA', payload: data });
            }}
            onSubmit={handleStep3Submit}
            isLoading={state.isLoading}
            onBack={goToPreviousStep}
            onStepClick={onStepClick}
          />
        );
      default:
        return (
          <Main
            setIsJobDescriptionModalOpen={setIsJobDescriptionModalOpen}
            setSelectedFiles={setSelectedFiles}
            setCurrentStep={onStepClick}
            setSelectedProposal={setSelectedProposal}
            orderByOptions={orderByOptions}
            setOrderBy={setOrderBy}
            orderBy={orderBy}
          />
        );
    }
  };

  return (
    <div className={styles.container}>
      <Stack horizontal className={styles.chatRoot}>
        <Stack horizontal tokens={{ childrenGap: 20 }} style={{ width: '100%' }}>
          <Stack.Item grow={1} style={{ height: '100%' }}>
            <div
              style={{
                height: '100%',
                padding: '2rem',
                borderRadius: '1rem',
                background: 'white',
              }}
            >
              {loadingAIDocuments ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <Loader />
                </div>
              ) : (
                renderWorkflowStep()
              )}
            </div>
          </Stack.Item>
        </Stack>
      </Stack>
      <ClientProposalModal
        files={selectedFiles}
        isOpen={isJobDescriptionModalOpen}
        setIsOpen={setIsJobDescriptionModalOpen}
        onUpload={handleUpload}
      />
    </div>
  );
};

export default ClientProposal;
